import { z } from 'zod';
import { isoDateSchema } from '../api';
import { APPOINTMENT_CHANNEL, APPOINTMENT_STATUS, APPOINTMENT_TYPE } from '../enums';
import { APPOINTMENT_FEE_ALERT, listCursorSchema } from './operations';
import { APPOINTMENT_CANCEL_REASON, APPOINTMENT_PRIORITY, APPOINTMENT_PRIORITY_REASON } from '../appointment-transitions';
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:mm');
export const scheduleSlotSchema = z.object({
    weekday: z.number().int().min(0).max(6),
    startTime: hhmm,
    endTime: hhmm,
    maxOverbook: z.number().int().min(0).max(20).default(0),
    portalBookable: z.boolean().default(true),
    capacity: z.number().int().min(1).max(50).default(1),
    maxWalkIns: z.number().int().min(0).max(200).default(0),
    roomLabel: z.string().trim().max(60).optional(),
    portalMaxPerSession: z.number().int().min(0).max(500).nullable().optional(),
});
export const upsertDoctorScheduleSchema = z.object({
    doctorId: z.string().uuid(),
    branchId: z.string().uuid(),
    departmentId: z.string().uuid().nullable().optional(),
    replacesScheduleId: z.string().uuid().optional(),
    slots: z.array(scheduleSlotSchema).min(1).max(120),
    isActive: z.boolean().default(true),
});
export const scheduleBlockSchema = z.object({
    doctorId: z.string().uuid(),
    branchId: z.string().uuid().nullable().optional(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    reason: z.enum(['leave', 'conference', 'surgery', 'other']).default('leave'),
    notes: z.string().max(400).optional(),
});
const appointmentDurationSchema = z.number().int().min(5).max(1440);
export const availabilityQuerySchema = z.object({
    doctorId: z.string().uuid(),
    branchId: z.string().uuid().optional(),
    date: isoDateSchema,
    durationMinutes: z.coerce.number().int().min(5).max(1440).optional(),
});
export const bookAppointmentSchema = z.object({
    patientId: z.string().uuid(),
    doctorId: z.string().uuid(),
    branchId: z.string().uuid(),
    departmentId: z.string().uuid().nullable().optional(),
    slotAt: z.string().datetime(),
    chargeItemId: z.string().uuid().nullable().optional(),
    durationMinutes: appointmentDurationSchema.optional(),
    type: z.enum(APPOINTMENT_TYPE).default('new'),
    channel: z.enum(APPOINTMENT_CHANNEL).default('desk'),
    reason: z.string().trim().max(400).optional(),
    /** Skip the "slot is full" check; requires appointment.manage plus overbook allowance. */
    allowOverbook: z.boolean().default(false),
    notifyPatient: z.boolean().default(true),
    payerId: z.string().uuid().nullable().optional(),
    coverageId: z.string().uuid().nullable().optional(),
    priority: z.enum(APPOINTMENT_PRIORITY).default('routine'),
    priorityReason: z.enum(APPOINTMENT_PRIORITY_REASON).nullable().optional(),
    packageId: z.string().uuid().nullable().optional(),
});
export const followUpEntitlementQuerySchema = z.object({
    patientId: z.string().uuid(),
    doctorId: z.string().uuid(),
    branchId: z.string().uuid().optional(),
    slotAt: z.string().datetime(),
});
export const lastVisitPayerQuerySchema = z.object({
    patientId: z.string().uuid(),
});
export const setVisitPayerSchema = z.object({
    payerId: z.string().uuid().nullable(),
    coverageId: z.string().uuid().nullable().optional(),
    reason: z.string().trim().min(5, 'Say why the payer is changing').max(400),
});
export const walkInSchema = z
    .object({
    patientId: z.string().uuid().optional(),
    newPatient: z
        .object({
        fullName: z.string().trim().min(2).max(160),
        phone: z.string().trim().min(7).max(20),
        sex: z.enum(['male', 'female', 'other']),
        ageYears: z.number().int().min(0).max(130).optional(),
        dob: isoDateSchema.optional(),
    })
        .optional(),
    confirmNew: z.boolean().default(false),
    doctorId: z.string().uuid(),
    branchId: z.string().uuid(),
    type: z.enum(APPOINTMENT_TYPE).default('new'),
    reason: z.string().trim().max(400).optional(),
    priority: z.enum(APPOINTMENT_PRIORITY).default('routine'),
    priorityReason: z.enum(APPOINTMENT_PRIORITY_REASON).nullable().optional(),
    payerId: z.string().uuid().nullable().optional(),
})
    .superRefine((value, context) => {
    if (!value.patientId && !value.newPatient) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['patientId'], message: 'Pick the patient or register them' });
    }
    if (value.newPatient && value.newPatient.ageYears === undefined && !value.newPatient.dob) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['newPatient', 'ageYears'], message: 'Enter the age or the date of birth' });
    }
});
export const arriveSchema = z.object({
    payerId: z.string().uuid().nullable().optional(),
    coverageId: z.string().uuid().nullable().optional(),
});
export const callPatientSchema = z.object({
    room: z.string().trim().max(40).optional(),
});
export const callNextSchema = z.object({
    doctorId: z.string().uuid(),
    room: z.string().trim().max(40).optional(),
});
export const setPrioritySchema = z.object({
    priority: z.enum(APPOINTMENT_PRIORITY),
    priorityReason: z.enum(APPOINTMENT_PRIORITY_REASON).nullable().optional(),
});
export const bulkRescheduleSchema = z.object({
    moves: z
        .array(z.object({
        appointmentId: z.string().uuid(),
        slotAt: z.string().datetime(),
        doctorId: z.string().uuid().optional(),
        durationMinutes: appointmentDurationSchema.optional(),
    }))
        .min(1)
        .max(100),
    reason: z.string().trim().max(400).optional(),
    notifyPatient: z.boolean().default(true),
});
export const blockImpactQuerySchema = z.object({
    doctorId: z.string().uuid(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
});
export const dayBoardQuerySchema = z.object({
    branchId: z.string().uuid(),
    date: isoDateSchema,
    page: z.coerce.number().int().min(1).max(50).default(1),
});
export const rescheduleAppointmentSchema = z.object({
    slotAt: z.string().datetime(),
    doctorId: z.string().uuid().optional(),
    durationMinutes: appointmentDurationSchema.optional(),
    reason: z.string().trim().max(400).optional(),
    notifyPatient: z.boolean().default(true),
});
export const updateAppointmentStatusSchema = z.object({
    status: z.enum(APPOINTMENT_STATUS),
    reasonCode: z.enum(APPOINTMENT_CANCEL_REASON).optional(),
    reason: z.string().trim().max(400).optional(),
    notifyPatient: z.boolean().default(true),
});
export const resolveFeeAlertSchema = z.union([
    z.object({ refundId: z.string().uuid() }).strict(),
    z.object({ creditNoteId: z.string().uuid() }).strict(),
    z
        .object({
        waived: z.literal(true),
        reason: z.string().trim().min(10, 'Say in a sentence why no refund or credit note is needed').max(400),
    })
        .strict(),
]);
export const appointmentQuerySchema = z.object({
    date: isoDateSchema.optional(),
    from: z.string().datetime().optional(),
    to: z.string().datetime().optional(),
    doctorId: z.string().uuid().optional(),
    patientId: z.string().uuid().optional(),
    branchId: z.string().uuid().optional(),
    departmentId: z.string().uuid().optional(),
    status: z.enum(APPOINTMENT_STATUS).optional(),
    feeAlert: z.enum(['any', ...APPOINTMENT_FEE_ALERT]).optional(),
    q: z.string().trim().max(120).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    cursor: listCursorSchema.optional(),
});
export function queueStage(row) {
    if (row.status !== 'arrived')
        return row.status;
    if (row.calledAt)
        return 'called';
    if (row.readyAt)
        return 'ready';
    if (row.hasVitals || row.hasScreening)
        return 'with_nurse';
    return 'waiting';
}
//# sourceMappingURL=appointments.js.map